import axios from 'axios';
import { dateTime, costTotal, creditTotal, creditTransactionTypes, signBasedTransactionTypes } from '../constants';
import { lineItemMessages } from '../messages';
import { trackViewBalanceHistoryStart } from '../tracking-events';
import { FIRST_PAGE, PAGE_SIZE_250 } from '../../../../services/pagination-service';

const determineCostOrCredit = (transactionType, transactionTotal) => {
  if (signBasedTransactionTypes.includes(transactionType)) {
    return transactionTotal.value > 0 ? creditTotal : costTotal;
  }
  return creditTransactionTypes.includes(transactionType) ? creditTotal : costTotal;
};

const modify = (historyData) => {
  const { pageData, ...restHistory } = historyData;
  const newResults = pageData.map((result) => {
    const { transactionTotal, ...rest } = result;
    const costOrCredit = determineCostOrCredit(rest.transactionType, transactionTotal);
    return {
      ...rest,
      // We don't want to show transactionIds to users, so instead of using them for the link to
      // transaction details, we will merge the ids into the date cell and use the date cell as the link.
      [dateTime]: {
        dateTimeValue: rest[dateTime],
        transactionId: rest.transactionId,
      },
      [costOrCredit]: transactionTotal,
    };
  });
  return { ...restHistory, pageData: newResults };
};

const defaultParams = {
  pagination: {
    pageSize: PAGE_SIZE_250,
    pageNumber: FIRST_PAGE,
  },
};

export const addDefaultParams = (params) => ({ ...defaultParams, ...params });

export const getTransactionHistory = async (walletId, parameters = {}) => {
  const params = addDefaultParams(parameters);
  trackViewBalanceHistoryStart(walletId, params);
  try {
    const response = await axios.post(`/wallet/${walletId}/transactions`, params);
    return modify(response.data);
  } catch (error) {
    throw new Error(`Error getting transaction history for wallet (${walletId}) : ${error?.message}`);
  }
};

export const exportTransactionHistory = async (walletId, parameters = {}) => {
  const params = addDefaultParams(parameters);
  const request = axios.post(`/wallet/${walletId}/transactions/export`, params).then((resp) => resp.data);
  return request;
};

export const getTransactionDetails = async (walletId, transactionId) => {
  try {
    // NOTE: the data provided by this call do not represent a complete/actual "salesOrder" or "fulfillment",
    //       only containing the ids for associated entities
    const response = await axios.get(`/wallet/${walletId}/transactions/${transactionId}`);

    /* Because of limitations in the way the balance history service is designed,
    we expect a one to one relationship between transactions and fulfillments,
    with a chance of that changing to many fulfillments per transaction in the future. */
    const fulfillmentDetails = response.data?.fulfillments?.reduce(
      (acc, { lineItems, trackingNumber, trackingUrl, carrierId, insuranceProviderId }) => {
        const trackingDetails = { trackingUrl, trackingNumber };
        const correctedLineItems = lineItems?.map((lineItem) => ({
          ...lineItem,
          description: lineItem.description || lineItemMessages[response.data?.transactionType],
        }));
        return {
          lineItems: correctedLineItems ? [...acc.lineItems, ...correctedLineItems] : acc.lineItems,
          carrierIds: carrierId ? [...acc.carrierIds, carrierId] : acc.carrierIds,
          insuranceProviderIds: insuranceProviderId ? [...acc.insuranceProviderIds, insuranceProviderId] : acc.insuranceProviderIds,
          trackingLinks: trackingNumber && trackingUrl ? [...acc.trackingLinks, trackingDetails] : acc.trackingLinks,
        };
      },
      {
        lineItems: [],
        trackingLinks: [],
        carrierIds: [],
        insuranceProviderIds: [],
      },
    );
    const flattenedDetails = { ...response.data, ...fulfillmentDetails };

    // See in ../transaction-details/index.js the transactionDetails modal checks if values are preset in transactionDetails to determine
    // whether or not to render the component for that detail. To avoid showing empty items, remove any empty arrays.
    const transactionDetails = Object.entries(flattenedDetails).reduce((_details, [detailKey, value]) => {
      // if the detail value is not an array, or is an Array and has items in it, add it to transactionDetails.
      if (!Array.isArray(value) || value.length) {
        return {
          ..._details,
          [detailKey]: value,
        };
      }
      return _details;
    }, {});

    /* This at least gives us one "line item" to show in the modal, as the balance history service
    doesn't consolidate related transactions, so a purchase of a label and insurance is going to be two separate transactions.
    each will just have one line item for their type. */
    if (!fulfillmentDetails.lineItems.length) {
      transactionDetails.lineItems = [
        {
          amount: transactionDetails.transactionTotal,
          description: lineItemMessages[transactionDetails.transactionType],
        },
      ];
    }

    return transactionDetails;
  } catch (error) {
    throw new Error(`Error getting transaction details for transaction (${transactionId}) : ${error?.message}`);
  }
};
